'use client';
import * as React from 'react';
import invariant from 'invariant';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DataGridProProps } from '@mui/x-data-grid-pro';
import { DataGridPremiumProps } from '@mui/x-data-grid-premium';
import {
  DataGrid,
  GridColDef,
  GridToolbar,
  gridClasses,
  DataGridProps,
  GridSortModel,
  GridFilterModel,
  GridEventListener,
  GridActionsCellItem,
  GridPaginationModel,
} from '@mui/x-data-grid';

import { NoSsr } from '../shared/NoSsr';
import { useDialogs } from '../useDialogs';
import { useActivePage } from '../useActivePage';
import { useNotifications } from '../useNotifications';
import { CrudContext, RouterContext } from '../shared/context';
import { useLocaleText } from '../AppProvider/LocalizationProvider';
import { PageContainer, type PageContainerProps } from '../PageContainer';

import { DataSourceCache } from './cache';
import { useCachedDataSource } from './useCachedDataSource';
import { DataModel, DataSource, DataModelId } from './types';
import { type CRUDLocaleText, CRUD_DEFAULT_LOCALE_TEXT } from './localeText';

export interface ListSlotProps {
  pageContainer?: PageContainerProps;
  dataGrid?: Partial<DataGridProps | DataGridProProps | DataGridPremiumProps>;
}

export interface ListSlots {
  pageContainer?: React.JSXElementConstructor<PageContainerProps>;
  /**
   * The DataGrid component used to list the items.
   * @default DataGrid
   */
  dataGrid?:
    | React.JSXElementConstructor<DataGridProps>
    | React.JSXElementConstructor<DataGridProProps>
    | React.JSXElementConstructor<DataGridPremiumProps>;
}

export interface ListProps<D extends DataModel> {
  /**
   * The components used for each slot inside.
   * @default {}
   */
  slots?: ListSlots;
  /**
   * The title of the page.
   */
  pageTitle?: string;
  /**
   * Initial number of rows to show per page.
   * @default 100
   */
  initialPageSize?: number;
  /**
   * The props used for each slot inside.
   * @default {}
   */
  slotProps?: ListSlotProps;
  /**
   * Callback fired when the "Create" button is clicked.
   */
  onCreateClick?: () => void;
  /**
   * Locale text for the component.
   */
  localeText?: CRUDLocaleText;
  /**
   * Additional actions displayed beside the Create button above the grid.
   */
  toolbarActions?: React.ReactNode;
  /**
   * Callback fired when the item is successfully deleted.
   */
  onDelete?: (id: DataModelId) => void;
  /**
   * Callback fired when a row is clicked. Not called if the target clicked is an interactive element added by the built-in columns.
   */
  onRowClick?: (id: DataModelId) => void;
  /**
   * Callback fired when the "Edit" button is clicked.
   */
  onEditClick?: (id: DataModelId) => void;
  /**
   * [Cache](https://mui.com/toolpad/core/react-crud/#data-caching) for the data source.
   */
  dataSourceCache?: DataSourceCache | null;
  /**
   * Server-side [data source](https://mui.com/toolpad/core/react-crud/#data-sources).
   */
  dataSource?: DataSource<D> & Required<Pick<DataSource<D>, 'getMany'>>;
}

/**
 *
 * Demos:
 *
 * - [CRUD](https://mui.com/toolpad/core/react-crud/)
 *
 * API:
 *
 * - [List API](https://mui.com/toolpad/core/api/list)
 */
function List<D extends DataModel>(props: ListProps<D>) {
  const {
    slots,
    onDelete,
    pageTitle,
    slotProps,
    onRowClick,
    onEditClick,
    onCreateClick,
    toolbarActions,
    dataSourceCache,
    initialPageSize = 100,
    localeText: propsLocaleText,
  } = props;

  const globalLocaleText = useLocaleText();
  const localeText = {
    ...CRUD_DEFAULT_LOCALE_TEXT,
    ...globalLocaleText,
    ...propsLocaleText,
  };

  const crudContext = React.useContext(CrudContext);
  const dataSource = (props.dataSource ??
    crudContext.dataSource) as NonNullable<typeof props.dataSource>;

  invariant(dataSource, 'No data source found.');

  const cache = React.useMemo(() => {
    const manualCache = dataSourceCache ?? crudContext.dataSourceCache;
    return typeof manualCache !== 'undefined'
      ? manualCache
      : new DataSourceCache();
  }, [crudContext.dataSourceCache, dataSourceCache]);
  const cachedDataSource = useCachedDataSource<D>(
    dataSource,
    cache
  ) as NonNullable<typeof props.dataSource>;

  const { fields, ...methods } = cachedDataSource;
  const { getMany, deleteOne } = methods;

  const routerContext = React.useContext(RouterContext);

  const activePage = useActivePage();

  const dialogs = useDialogs();
  const notifications = useNotifications();

  const [paginationModel, setPaginationModel] =
    React.useState<GridPaginationModel>({
      page: routerContext?.searchParams.get('page')
        ? Number(routerContext?.searchParams.get('page'))
        : 0,
      pageSize: routerContext?.searchParams.get('pageSize')
        ? Number(routerContext?.searchParams.get('pageSize'))
        : initialPageSize,
    });
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>(
    routerContext?.searchParams.get('filter')
      ? JSON.parse(routerContext?.searchParams.get('filter') ?? '')
      : { items: [] }
  );
  const [sortModel, setSortModel] = React.useState<GridSortModel>(
    routerContext?.searchParams.get('sort')
      ? JSON.parse(routerContext?.searchParams.get('sort') ?? '')
      : []
  );

  const cachedData = React.useMemo(
    () =>
      cache &&
      (cache.get(
        JSON.stringify([
          'getMany',
          {
            sortModel,
            filterModel,
            paginationModel,
          },
        ])
      ) as {
        items: D[];
        itemCount: number;
      }),
    [cache, filterModel, paginationModel, sortModel]
  );

  const [rowsState, setRowsState] = React.useState<{
    rows: D[];
    rowCount: number;
  }>({
    rows: cachedData?.items ?? [],
    rowCount: cachedData?.itemCount ?? 0,
  });
  const [isLoading, setIsLoading] = React.useState(!cachedData);
  const [error, setError] = React.useState<Error | null>(null);

  const handlePaginationModelChange = React.useCallback(
    (model: GridPaginationModel) => {
      setPaginationModel(model);

      if (routerContext) {
        const { pathname, navigate, searchParams } = routerContext;

        // Needed because searchParams from Next.js are read-only
        const writeableSearchParams = new URLSearchParams(searchParams);

        // FIX: Use 'model' parameter instead of stale 'paginationModel' state
        writeableSearchParams.set('page', String(model.page));
        writeableSearchParams.set('pageSize', String(model.pageSize));

        const newSearchParamsString = writeableSearchParams.toString();

        navigate(
          `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`
        );
      }
    },
    [routerContext]
  );

  const handleFilterModelChange = React.useCallback(
    (model: GridFilterModel) => {
      setFilterModel(model);

      if (routerContext) {
        const { pathname, navigate, searchParams } = routerContext;

        // Needed because searchParams from Next.js are read-only
        const writeableSearchParams = new URLSearchParams(searchParams);

        // FIX: Use 'model' parameter instead of stale 'filterModel' state
        if (
          model.items.length > 0 ||
          (model.quickFilterValues && model.quickFilterValues.length > 0)
        ) {
          writeableSearchParams.set('filter', JSON.stringify(model));
        } else {
          writeableSearchParams.delete('filter');
        }

        const newSearchParamsString = writeableSearchParams.toString();

        navigate(
          `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`
        );
      }
    },
    [routerContext]
  );

  const handleSortModelChange = React.useCallback(
    (model: GridSortModel) => {
      setSortModel(model);

      if (routerContext) {
        const { pathname, navigate, searchParams } = routerContext;

        // Needed because searchParams from Next.js are read-only
        const writeableSearchParams = new URLSearchParams(searchParams);

        // FIX: Use 'model' parameter instead of stale 'sortModel' state
        if (model.length > 0) {
          writeableSearchParams.set('sort', JSON.stringify(model));
        } else {
          writeableSearchParams.delete('sort');
        }

        const newSearchParamsString = writeableSearchParams.toString();

        navigate(
          `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`
        );
      }
    },
    [routerContext]
  );

  const loadData = React.useCallback(async () => {
    setError(null);

    let listData = cachedData;
    if (!listData) {
      setIsLoading(true);

      try {
        listData = await getMany({
          sortModel,
          filterModel,
          paginationModel,
        });
      } catch (listDataError) {
        setError(listDataError as Error);
      }
    }

    if (listData) {
      setRowsState({
        rows: listData.items,
        rowCount: listData.itemCount,
      });
    }
    setIsLoading(false);
  }, [cachedData, filterModel, getMany, paginationModel, sortModel]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = React.useCallback(() => {
    if (!isLoading) {
      cache?.clear();
      loadData();
    }
  }, [cache, isLoading, loadData]);

  const handleRowClick = React.useCallback<GridEventListener<'rowClick'>>(
    ({ row }) => {
      if (onRowClick) {
        onRowClick(row.id);
      }
    },
    [onRowClick]
  );

  const handleItemEdit = React.useCallback(
    (itemId: DataModelId) => () => {
      if (onEditClick) {
        onEditClick(itemId);
      }
    },
    [onEditClick]
  );

  const handleItemDelete = React.useCallback(
    (itemId: DataModelId) => async () => {
      const confirmed = await dialogs.confirm(localeText.deleteConfirmMessage, {
        severity: 'error',
        title: localeText.deleteConfirmTitle,
        okText: localeText.deleteConfirmLabel,
        cancelText: localeText.deleteCancelLabel,
      });

      if (confirmed) {
        setIsLoading(true);
        try {
          await deleteOne?.(itemId);

          if (onDelete) {
            onDelete(itemId);
          }

          notifications.show(localeText.deleteSuccessMessage, {
            severity: 'success',
            autoHideDuration: 3000,
          });
          loadData();
        } catch (deleteError) {
          notifications.show(
            `${localeText.deleteErrorMessage} ${(deleteError as Error).message}`,
            {
              severity: 'error',
              autoHideDuration: 3000,
            }
          );
        }
        setIsLoading(false);
      }
    },
    [
      deleteOne,
      dialogs,
      loadData,
      localeText.deleteCancelLabel,
      localeText.deleteConfirmLabel,
      localeText.deleteConfirmMessage,
      localeText.deleteConfirmTitle,
      localeText.deleteErrorMessage,
      localeText.deleteSuccessMessage,
      notifications,
      onDelete,
    ]
  );

  const DataGridSlot = slots?.dataGrid ?? DataGrid;
  const PageContainerSlot = slots?.pageContainer ?? PageContainer;

  const initialState = React.useMemo(
    () => ({
      pagination: { paginationModel: { pageSize: initialPageSize } },
    }),
    [initialPageSize]
  );

  const columns = React.useMemo<GridColDef[]>(() => {
    const pinnedColumnsOverride = (
      slotProps?.dataGrid as DataGridProProps | DataGridPremiumProps
    )?.initialState?.pinnedColumns;
    const isActionsColumnPinned =
      pinnedColumnsOverride?.left?.includes('actions') ||
      pinnedColumnsOverride?.right?.includes('actions');

    return [
      ...fields.map((field) => ({
        ...field,
        editable: false,
      })),
      {
        align: 'right',
        type: 'actions',
        field: 'actions',
        flex: isActionsColumnPinned ? undefined : 1,
        getActions: ({ id }) => [
          ...(onEditClick
            ? [
                <GridActionsCellItem
                  key="edit-item"
                  icon={<EditIcon />}
                  label={localeText.editLabel}
                  onClick={handleItemEdit(id)}
                />,
              ]
            : []),
          ...(deleteOne
            ? [
                <GridActionsCellItem
                  key="delete-item"
                  icon={<DeleteIcon />}
                  label={localeText.deleteLabel}
                  onClick={handleItemDelete(id)}
                />,
              ]
            : []),
        ],
      },
    ];
  }, [
    deleteOne,
    fields,
    handleItemDelete,
    handleItemEdit,
    localeText.deleteLabel,
    localeText.editLabel,
    onEditClick,
    slotProps?.dataGrid,
  ]);

  return (
    <PageContainerSlot
      title={pageTitle}
      breadcrumbs={
        activePage && pageTitle
          ? [
              ...activePage.breadcrumbs,
              {
                title: pageTitle,
              },
            ]
          : undefined
      }
      {...slotProps?.pageContainer}
    >
      <Stack sx={{ flex: 1, width: '100%' }}>
        {error ? (
          <Box sx={{ flexGrow: 1 }}>
            <Alert severity="error">{error.message}</Alert>
          </Box>
        ) : (
          <React.Fragment>
            <Stack
              direction="row"
              sx={{
                mb: 1,
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Tooltip
                placement="right"
                enterDelay={1000}
                title={localeText.reloadButtonLabel}
              >
                <div>
                  <IconButton aria-label="refresh" onClick={handleRefresh}>
                    <RefreshIcon />
                  </IconButton>
                </div>
              </Tooltip>
              <Stack
                direction="row"
                sx={{ gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}
              >
                {toolbarActions}
                {onCreateClick ? (
                  <Button
                    variant="contained"
                    onClick={onCreateClick}
                    startIcon={<AddIcon />}
                  >
                    {localeText.createNewButtonLabel}
                  </Button>
                ) : null}
              </Stack>
            </Stack>
            {/* Use NoSsr to prevent issue https://github.com/mui/mui-x/issues/17077 as DataGrid has no SSR support */}
            <NoSsr>
              <DataGridSlot
                pagination
                columns={columns}
                filterMode="server"
                loading={isLoading}
                sortingMode="server"
                rows={rowsState.rows}
                sortModel={sortModel}
                paginationMode="server"
                filterModel={filterModel}
                disableRowSelectionOnClick
                onRowClick={handleRowClick}
                initialState={initialState}
                rowCount={rowsState.rowCount}
                slots={{ toolbar: GridToolbar }}
                paginationModel={paginationModel}
                onSortModelChange={handleSortModelChange}
                onFilterModelChange={handleFilterModelChange}
                onPaginationModelChange={handlePaginationModelChange}
                // Prevent type conflicts if slotProps don't match DataGrid used for dataGrid slot
                {...(slotProps?.dataGrid as Record<string, unknown>)}
                sx={{
                  [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
                    outline: 'transparent',
                  },
                  [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
                    {
                      outline: 'none',
                    },
                  ...(onRowClick
                    ? {
                        [`& .${gridClasses.row}:hover`]: {
                          cursor: 'pointer',
                        },
                      }
                    : {}),
                  ...slotProps?.dataGrid?.sx,
                }}
              />
            </NoSsr>
          </React.Fragment>
        )}
      </Stack>
    </PageContainerSlot>
  );
}

List.propTypes /* remove-proptypes */ = {
  /**
   * Callback fired when the item is successfully deleted.
   */
  onDelete: PropTypes.func,
  /**
   * Callback fired when a row is clicked. Not called if the target clicked is an interactive element added by the built-in columns.
   */
  onRowClick: PropTypes.func,
  /**
   * Callback fired when the "Edit" button is clicked.
   */
  onEditClick: PropTypes.func,
  /**
   * The title of the page.
   */
  pageTitle: PropTypes.string,
  // ┌────────────────────────────── Warning ──────────────────────────────┐
  // │ These PropTypes are generated from the TypeScript type definitions. │
  // │ To update them, edit the TypeScript types and run `pnpm proptypes`. │
  // └─────────────────────────────────────────────────────────────────────┘
  /**
   * Server-side [data source](https://mui.com/toolpad/core/react-crud/#data-sources).
   */
  dataSource: PropTypes.object,
  /**
   * Locale text for the component.
   */
  localeText: PropTypes.object,
  /**
   * Callback fired when the "Create" button is clicked.
   */
  onCreateClick: PropTypes.func,
  /**
   * Initial number of rows to show per page.
   * @default 100
   */
  initialPageSize: PropTypes.number,
  /**
   * The components used for each slot inside.
   * @default {}
   */
  slots: PropTypes.shape({
    dataGrid: PropTypes.func,
    pageContainer: PropTypes.elementType,
  }),
  /**
   * The props used for each slot inside.
   * @default {}
   */
  slotProps: PropTypes.shape({
    dataGrid: PropTypes.object,
    pageContainer: PropTypes.object,
  }),
  /**
   * [Cache](https://mui.com/toolpad/core/react-crud/#data-caching) for the data source.
   */
  dataSourceCache: PropTypes.shape({
    get: PropTypes.func.isRequired,
    set: PropTypes.func.isRequired,
    clear: PropTypes.func.isRequired,
    ttl: PropTypes.number.isRequired,
    cache: PropTypes.object.isRequired,
  }),
} as any;

export { List };
